import i18n from '@/utils/i18n';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ChatStatus,
  SalesRepOverrideMode,
  VariantPackAvailabilityStatus,
} from '@/enums';
import { DEFAULT_CURRENCY_CODE, getCountryCurrencyCode } from '@/utils/money';
import {
  GET_ESSENTIAL_ITEMS_QUERY,
  GET_ORDER_CONSTRAINTS_QUERY,
  GET_ORDER_GUIDE_VARIANT_PACK_INFO_LIST_QUERY,
  GET_VARIANT_PACK_INFO_LIST_QUERY,
  GET_VARIANT_PACK_LAST_PURCHASE_INFO_QUERY,
} from './variant-pack-info-context-queries';
import { getTranslationValue } from '@/utils/translations';
import {
  GroupMap,
  useVariantPackGroup,
  VariantPackGroupMap,
} from '@/hooks/use-variant-pack-groups';
import { isMinMaxQuantityEnabled } from '@/hooks/use-min-max-quantity-enabled';
import {
  PriceEditDisplayInfoDetail,
  PriceEditTitleTranslation,
  PriceEditVariantPackDetail,
  PriceEditVariantPackDisplaySettings,
  VariantPackConstraint,
  VariantPackCostInfoDetailsFieldsFragment,
  VariantPackInfoContext_GetEssentialItemsQuery,
  VariantPackInfoContext_GetEssentialItemsQueryVariables,
  VariantPackInfoContext_InfoFieldsFragment,
  VariantPackInfoContext_InfoListQuery,
  VariantPackInfoContext_InfoListQueryVariables,
  VariantPackInfoContext_LastPurchaseInfoQuery,
  VariantPackInfoContext_LastPurchaseInfoQueryVariables,
  VariantPackInfoContext_OrderConstraintsQuery,
  VariantPackInfoContext_OrderConstraintsQueryVariables,
  VariantPackInfoContext_OrderGuideInfoListQuery,
  VariantPackInfoContext_OrderGuideInfoListQueryVariables,
} from '@/types';
import { PriceEditInfo } from '@/adapters/offline-mode';
import { SalesRepInfoCostMap } from '@/domain/pricing/types';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import {
  useGetOrderGuidePriceEditDisplayInfo,
  useGetPriceEditDisplayInfo,
} from '@/components/orders/hooks/use-get-price-edit-display-info';
import { useNewOrderActionsContext } from './new-order-actions-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOfflineModeEnabled } from '@/hooks/use-offline-mode-enabled';
import {
  useOfflineOrderConstraints,
  useOfflineVariantPackInfo,
} from '@/hooks/use-offline-data';
import { useOfflinePriceEdits } from '@/hooks/use-offline-price-edits';
import { useSalesRepMode } from '@/hooks/use-sales-rep-mode';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from './user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export interface VariantPackInfoType {
  availabilityStatus: VariantPackAvailabilityStatus | null;
  availableQuantity: number | null;
  constraints: VariantPackConstraint | null;
  currencyCode: string;
  listItemDisplayText: string | null;
  manufacturerSuggestedRetailPriceInMicros: number | null;
  priceInMicros: number | null;
  salesRepInfo?: {
    ceilingPriceInMicros: number | null;
    costMap: SalesRepInfoCostMap;
    defaultPriceInMicros: number | null;
    inventory: {
      incoming: { date: string; quantity: number }[];
      onHandQuantity: number | null;
    };
    salesRepGrossProfitOverrideInPercentage: number | null;
    salesRepOverrideUpdatedByName?: string | null;
    salesRepPriceInMicros: number | null;
  };
  suggestedRetailPriceInMicros: number | null;
  unitCount: number;
}

export interface LastPurchaseInfoType {
  costMap: SalesRepInfoCostMap;
  fulfillmentDate: string | null;
  orderDate: string | null;
  priceInMicros: number | null;
  quantity: number | null;
  unitCount: number | null;
}

interface VariantPackInfoMap {
  [variantPackUUID: string]: VariantPackInfoType;
}

interface LastPurchaseInfoMap {
  [variantPackUUID: string]: LastPurchaseInfoType | undefined;
}

interface VariantPackDetails {
  [variantPackUUID: string]: Array<PriceEditDisplayInfoDetail> | undefined;
}

interface VariantPackPriceEditSettingsMap {
  [variantPackUUID: string]: PriceEditVariantPackDisplaySettings | null;
}

interface VariantPackOrderConstraintsMap {
  [variantPackUUID: string]: VariantPackConstraint | null;
}

export interface VariantPackInfoContextType {
  childVariantPackGroupMap: GroupMap;
  currencyCode: string | null;
  essentialVariantPackUUIDs: Set<string>;
  getVariantPackInfo: (variantPackUUID: string) => VariantPackInfoType | null;
  hasInfoListResponse: boolean;
  hasLastPurchaseInfoResponse: boolean;
  lastPurchaseInfoMap: LastPurchaseInfoMap;
  parentVariantPackGroupMap: GroupMap;
  translatedTitles: { [key: string]: string };
  updateSalesRepVariantPackInfo: (
    variantPackUUID: string,
    info: VariantPackInfoContext_InfoFieldsFragment | null,
    offlinePriceEditInfo: PriceEditInfo | null,
  ) => VariantPackInfoContext_InfoFieldsFragment | null;
  variantPackDetails: VariantPackDetails;
  variantPackGroupMap: VariantPackGroupMap;
  variantPackPriceEditSettings: VariantPackPriceEditSettingsMap;
}

export const VariantPackInfoContext =
  createContext<VariantPackInfoContextType | null>(null);

const getVariantPackCostMap = (
  costInfoDetails: VariantPackCostInfoDetailsFieldsFragment[],
) => {
  return costInfoDetails.reduce<SalesRepInfoCostMap>((result, d) => {
    result[d.type] = d.cost_in_micros;
    return result;
  }, {});
};

const getVariantPackInfo = (
  info: VariantPackInfoContext_InfoFieldsFragment,
  salesRepMode: boolean,
  constraints: VariantPackConstraint | null,
) => {
  const variantPackInfo: VariantPackInfoType = {
    availabilityStatus: info.availability_status
      ? (info.availability_status as VariantPackAvailabilityStatus)
      : null,
    availableQuantity: info.available_quantity ?? null,
    constraints,
    currencyCode: info.currency_code,
    listItemDisplayText: info.list_item_display_text ?? null,
    manufacturerSuggestedRetailPriceInMicros:
      info.manufacturer_suggested_retail_price_in_micros ?? null,
    priceInMicros: info.price_in_micros ?? null,
    suggestedRetailPriceInMicros: info.suggested_retail_price_in_micros ?? null,
    unitCount: info.unit_count,
  };

  if (salesRepMode) {
    variantPackInfo.salesRepInfo = {
      ceilingPriceInMicros:
        info.sales_rep_metadata?.price_ceiling_in_micros ?? null,
      costMap: getVariantPackCostMap(info.cost_info?.details || []),
      defaultPriceInMicros:
        typeof info.default_price_in_micros === 'number'
          ? info.default_price_in_micros * info.unit_count
          : null,
      inventory: {
        incoming: (info.inventory_info?.incoming || []).map((i) => ({
          date: i.date,
          quantity: i.quantity,
        })),
        onHandQuantity: info.inventory_info?.quantity_on_hand ?? null,
      },
      salesRepGrossProfitOverrideInPercentage:
        info.sales_rep_gross_profit_in_percentage
          ? Number(info.sales_rep_gross_profit_in_percentage)
          : null,
      salesRepOverrideUpdatedByName:
        info.sales_rep_metadata?.updated_by_name ?? null,
      salesRepPriceInMicros: info.sales_rep_price_in_micros ?? null,
    };
  }

  return variantPackInfo;
};

const getVariantPackInfoMap = (
  infoList: VariantPackInfoContext_InfoFieldsFragment[],
  salesRepMode: boolean,
  constraintsMap: VariantPackOrderConstraintsMap,
) => {
  return infoList.reduce<VariantPackInfoMap>((acc, p) => {
    const constraints = constraintsMap[p.variant_pack_id];
    acc[p.variant_pack_id] = getVariantPackInfo(p, salesRepMode, constraints);
    return acc;
  }, {});
};

export const useVariantPackInfoContext = () => {
  const ctx = useContext(VariantPackInfoContext);
  if (!ctx) {
    throw new Error(
      'useVariantPackInfoContext requires a VariantPackInfoContextProvider',
    );
  }
  return ctx;
};

interface Props {
  children?: React.ReactNode;
  skipLastPurchaseInfo?: boolean;
}

const getTranslatedTitleForPriceEditDisplayInfo = (
  options: PriceEditTitleTranslation[],
): { [key: string]: string } => {
  if (!options) {
    return {};
  }
  return options.reduce<{ [key: string]: string }>((acc, option) => {
    acc[option.title] = getTranslationValue(JSON.parse(option.translation));
    return acc;
  }, {});
};

const getVariantPackSettingsForPriceEditDisplayInfo = (
  variant_pack_details: PriceEditVariantPackDetail[],
): VariantPackPriceEditSettingsMap => {
  return variant_pack_details.reduce<VariantPackPriceEditSettingsMap>(
    (acc, detail) => {
      acc[detail.variant_pack_id] = detail.settings ?? null;
      return acc;
    },
    {} as VariantPackPriceEditSettingsMap,
  );
};

export const VariantPackInfoContextProvider: React.FC<Props> = ({
  children,
  skipLastPurchaseInfo = false,
}) => {
  const { showErrorToast } = useToasts();
  const { fulfillmentHydrated, selectedFulfillment } =
    useNewOrderActionsContext();
  const userContext = useUserContext();
  const { selectedRestaurantRelationship } = userContext;
  const isProspect =
    selectedRestaurantRelationship.chatStatus === ChatStatus.PROSPECT;
  const salesRepMode = useSalesRepMode();
  const { fetchOfflinePriceEdits } = useOfflinePriceEdits();

  const employeeRole =
    'supplierEmployeeRole' in userContext
      ? userContext.supplierEmployeeRole
      : null;
  const orderQuantityConfig = useDynamicConfigValue('order_quantity_config');
  const minMaxQuantityEnabled = isMinMaxQuantityEnabled({
    employeeRole,
    orderQuantityConfig,
  });

  const [updatedSalesRepInfoMap, setUpdatedSalesRepInfoMap] =
    useState<VariantPackInfoMap>({});

  const isOfflineModeEnabled = useOfflineModeEnabled();
  const isOfflineModeActive = useOfflineModeActive();

  // Prevent duplicate toasts across shared onError callbacks
  const hasShownLoadErrorToast = React.useRef(false);
  const handleLoadInfoQueryError = useCallback(() => {
    if (!isOfflineModeActive && !hasShownLoadErrorToast.current) {
      hasShownLoadErrorToast.current = true;
      showErrorToast(i18n.t('toast.product_info_load_error'));
    }
  }, [isOfflineModeActive, showErrorToast]);

  const priceEditDisplay = useGetPriceEditDisplayInfo(
    salesRepMode,
    handleLoadInfoQueryError,
  );
  const orderGuidePriceEditDisplay = useGetOrderGuidePriceEditDisplayInfo(
    salesRepMode,
    handleLoadInfoQueryError,
  );

  const {
    childVariantPackGroupMap,
    parentVariantPackGroupMap,
    variantPackGroupMap,
  } = useVariantPackGroup();

  const chatUUID = selectedRestaurantRelationship.chatUUIDs[0];
  const restaurantUUID = selectedRestaurantRelationship.uuid;
  const supplierUUID = selectedRestaurantRelationship.supplierUUID;

  const essentialItemsResult = useWrappedQuery<
    VariantPackInfoContext_GetEssentialItemsQuery,
    VariantPackInfoContext_GetEssentialItemsQueryVariables
  >(GET_ESSENTIAL_ITEMS_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !restaurantUUID || !supplierUUID || isOfflineModeActive,
    variables: {
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });

  const orderGuideInfoListResult = useWrappedQuery<
    VariantPackInfoContext_OrderGuideInfoListQuery,
    VariantPackInfoContext_OrderGuideInfoListQueryVariables
  >(GET_ORDER_GUIDE_VARIANT_PACK_INFO_LIST_QUERY, {
    fetchPolicy: 'no-cache',
    onError: handleLoadInfoQueryError,
    skip:
      !fulfillmentHydrated ||
      !restaurantUUID ||
      !supplierUUID ||
      (!selectedFulfillment?.date && !isProspect) ||
      isOfflineModeActive,
    variables: {
      deliveryDate: selectedFulfillment?.date,
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });

  const infoListResult = useWrappedQuery<
    VariantPackInfoContext_InfoListQuery,
    VariantPackInfoContext_InfoListQueryVariables
  >(GET_VARIANT_PACK_INFO_LIST_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'no-cache',
    onError: handleLoadInfoQueryError,
    skip:
      !fulfillmentHydrated ||
      !restaurantUUID ||
      !supplierUUID ||
      (!selectedFulfillment?.date && !isProspect) ||
      isOfflineModeActive,
    variables: {
      deliveryDate: selectedFulfillment?.date,
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });

  const lastPurchaseInfoResult = useWrappedQuery<
    VariantPackInfoContext_LastPurchaseInfoQuery,
    VariantPackInfoContext_LastPurchaseInfoQueryVariables
  >(GET_VARIANT_PACK_LAST_PURCHASE_INFO_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'no-cache',
    skip: !chatUUID || isOfflineModeActive || skipLastPurchaseInfo,
    variables: {
      chatUUID: chatUUID!,
    },
  });

  const orderConstraintsResult = useWrappedQuery<
    VariantPackInfoContext_OrderConstraintsQuery,
    VariantPackInfoContext_OrderConstraintsQueryVariables
  >(GET_ORDER_CONSTRAINTS_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'no-cache',
    skip:
      !restaurantUUID ||
      !supplierUUID ||
      isOfflineModeActive ||
      !minMaxQuantityEnabled,
    variables: {
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });

  const offlineVariantPackInfoList = useOfflineVariantPackInfo(
    restaurantUUID,
    supplierUUID,
  );

  const combinedVariantPackInfoListData = useMemo(() => {
    return isOfflineModeActive && offlineVariantPackInfoList.data
      ? offlineVariantPackInfoList.data?.getVariantPackInfoList
      : [
          ...(orderGuideInfoListResult.data?.getVariantPackInfoList || []),
          ...(infoListResult.data?.getVariantPackInfoList || []),
        ];
  }, [
    isOfflineModeActive,
    offlineVariantPackInfoList.data,
    orderGuideInfoListResult.data?.getVariantPackInfoList,
    infoListResult.data?.getVariantPackInfoList,
  ]);

  const essentialVariantPackUUIDs = useMemo(
    () =>
      new Set(
        (essentialItemsResult.data?.getCriticalItems || []).map(
          (i) => i.variant_pack_id,
        ),
      ),
    [essentialItemsResult.data?.getCriticalItems],
  );

  const offlineOrderConstraints = useOfflineOrderConstraints(
    restaurantUUID,
    supplierUUID,
  );

  const variantPackConstraintsMap = useMemo(() => {
    if (!minMaxQuantityEnabled) {
      return {};
    }

    const orderConstraintsData =
      isOfflineModeActive && offlineOrderConstraints.data
        ? offlineOrderConstraints.data.getOrderConstraintsForRestaurant
        : orderConstraintsResult.data?.getOrderConstraintsForRestaurant;

    return (orderConstraintsData || []).reduce<VariantPackOrderConstraintsMap>(
      (acc, oc) => {
        const minMaxConstraint = oc.order_constraints?.find((constraint) => {
          return 'min_quantity' in constraint || 'max_quantity' in constraint;
        });
        acc[oc.variant_pack_id] = minMaxConstraint ?? null;
        return acc;
      },
      {},
    );
  }, [
    isOfflineModeActive,
    minMaxQuantityEnabled,
    offlineOrderConstraints,
    orderConstraintsResult,
  ]);

  const updateSalesRepVariantPackInfo = useCallback(
    (
      variantPackUUID: string,
      info: VariantPackInfoContext_InfoFieldsFragment | null,
      offlinePriceEditInfo: PriceEditInfo | null,
    ): VariantPackInfoContext_InfoFieldsFragment | null => {
      let latestVariantPackInfo = info;

      // Simulates the behavior of the catalog resolver when there is sales rep override
      if (!latestVariantPackInfo && offlinePriceEditInfo) {
        latestVariantPackInfo =
          combinedVariantPackInfoListData.find(
            (vp) => vp.variant_pack_id === variantPackUUID,
          ) ?? null;

        // Guard against undefined variant pack (e.g., if data hasn't loaded yet)
        if (!latestVariantPackInfo) {
          console.warn(
            `[VariantPackInfoContext] Variant pack not found: ${variantPackUUID}`,
          );
          return null;
        }

        if (offlinePriceEditInfo.type === SalesRepOverrideMode.PRICE) {
          latestVariantPackInfo.sales_rep_price_in_micros =
            offlinePriceEditInfo.value;

          if (offlinePriceEditInfo.value === null) {
            latestVariantPackInfo.price_in_micros =
              latestVariantPackInfo.default_price_in_micros;
            latestVariantPackInfo.sales_rep_gross_profit_in_percentage = null;
          } else {
            latestVariantPackInfo.price_in_micros = offlinePriceEditInfo.value;
          }
        } else if (
          offlinePriceEditInfo.type === SalesRepOverrideMode.GROSS_PROFIT
        ) {
          latestVariantPackInfo.sales_rep_gross_profit_in_percentage =
            offlinePriceEditInfo.value;

          if (offlinePriceEditInfo.value === null) {
            latestVariantPackInfo.price_in_micros =
              latestVariantPackInfo.default_price_in_micros;
            latestVariantPackInfo.sales_rep_price_in_micros = null;
          } else {
            const costMap = getVariantPackCostMap(
              latestVariantPackInfo.cost_info?.details || [],
            );
            const costInMicros =
              costMap[selectedRestaurantRelationship.customerCostType!] ?? 0;
            latestVariantPackInfo.price_in_micros =
              costInMicros / (1 - offlinePriceEditInfo.value / 100);
          }
        }
      }

      // Bail out if we have no info to derive from (e.g., the mutation
      // returned no data because a validation error was ignored).
      if (!latestVariantPackInfo) {
        return null;
      }

      const resolvedVariantPackInfo = latestVariantPackInfo;
      setUpdatedSalesRepInfoMap((previous) => ({
        ...previous,
        [variantPackUUID]: getVariantPackInfo(
          resolvedVariantPackInfo,
          salesRepMode,
          variantPackConstraintsMap[variantPackUUID],
        ),
      }));

      return resolvedVariantPackInfo;
    },
    [
      combinedVariantPackInfoListData,
      salesRepMode,
      selectedRestaurantRelationship.customerCostType,
      variantPackConstraintsMap,
    ],
  );

  useEffect(() => {
    (async () => {
      if (!isOfflineModeActive || !isOfflineModeEnabled) {
        return null;
      }

      const variantPacksPriceEditMap = await fetchOfflinePriceEdits(
        restaurantUUID,
        supplierUUID,
      );

      // Populate the updatedSalesRepInfoMap with the offline price edits
      for (const variantPackUUID in variantPacksPriceEditMap) {
        updateSalesRepVariantPackInfo(
          variantPackUUID,
          null,
          variantPacksPriceEditMap[variantPackUUID],
        );
      }
    })();
  }, [
    fetchOfflinePriceEdits,
    isOfflineModeActive,
    isOfflineModeEnabled,
    restaurantUUID,
    supplierUUID,
    updateSalesRepVariantPackInfo,
  ]);

  const value = useMemo<VariantPackInfoContextType>(() => {
    const findCurrencyCode = (vp: VariantPackInfoContext_InfoFieldsFragment) =>
      Boolean(vp.currency_code);

    const vpWithCurrencyCode =
      combinedVariantPackInfoListData.find(findCurrencyCode);

    const variantPackInfoMap = {
      ...getVariantPackInfoMap(
        combinedVariantPackInfoListData,
        salesRepMode,
        variantPackConstraintsMap,
      ),
      ...updatedSalesRepInfoMap,
    };

    const countryCode = selectedRestaurantRelationship.countryCode;

    const fallbackCurrencyCode = countryCode
      ? getCountryCurrencyCode(countryCode)
      : DEFAULT_CURRENCY_CODE;

    const initialVariantPackDetails = [
      ...(priceEditDisplay?.data?.getPriceEditDisplayInfo
        .variant_pack_details || []),
      ...(orderGuidePriceEditDisplay?.data?.getPriceEditDisplayInfo
        .variant_pack_details || []),
    ];

    const translatedTitles = {
      on_hand: i18n.t('on_hand'),
      ...getTranslatedTitleForPriceEditDisplayInfo(
        priceEditDisplay?.data?.getPriceEditDisplayInfo.title_translations!,
      ),
      ...getTranslatedTitleForPriceEditDisplayInfo(
        orderGuidePriceEditDisplay?.data?.getPriceEditDisplayInfo
          .title_translations!,
      ),
    };

    const variantPackDetails = initialVariantPackDetails.reduce((acc, d) => {
      acc[d.variant_pack_id] = d.detail;
      return acc;
    }, {} as VariantPackDetails);

    const variantPackPriceEditSettings = {
      ...getVariantPackSettingsForPriceEditDisplayInfo(
        priceEditDisplay?.data?.getPriceEditDisplayInfo.variant_pack_details ||
          [],
      ),
      ...getVariantPackSettingsForPriceEditDisplayInfo(
        orderGuidePriceEditDisplay?.data?.getPriceEditDisplayInfo
          .variant_pack_details || [],
      ),
    };

    return {
      childVariantPackGroupMap,
      currencyCode: vpWithCurrencyCode?.currency_code || fallbackCurrencyCode,
      essentialVariantPackUUIDs,
      getVariantPackInfo: (variantPackUUID: string) => {
        return variantPackInfoMap[variantPackUUID] ?? null;
      },
      hasInfoListResponse: infoListResult.hasResponse,
      hasLastPurchaseInfoResponse: lastPurchaseInfoResult.hasResponse,
      lastPurchaseInfoMap: (
        lastPurchaseInfoResult.data?.getVariantPackLastPurchaseInfo || []
      ).reduce<LastPurchaseInfoMap>((result, lastPurchaseInfo) => {
        const variantPackUUID = lastPurchaseInfo.variant_pack_id;
        if (!variantPackUUID) {
          return result;
        }

        result[variantPackUUID] = {
          costMap: getVariantPackCostMap(
            lastPurchaseInfo.cost_info?.details || [],
          ),
          fulfillmentDate: lastPurchaseInfo.fulfillment_date ?? null,
          orderDate: lastPurchaseInfo.order_date ?? null,
          priceInMicros: lastPurchaseInfo.price_in_micros ?? null,
          quantity: lastPurchaseInfo.quantity ?? null,
          unitCount: lastPurchaseInfo.unit_count ?? null,
        };

        return result;
      }, {}),
      parentVariantPackGroupMap,
      translatedTitles,
      updateSalesRepVariantPackInfo,
      variantPackDetails,
      variantPackGroupMap,
      variantPackPriceEditSettings,
    };
  }, [
    childVariantPackGroupMap,
    combinedVariantPackInfoListData,
    essentialVariantPackUUIDs,
    selectedRestaurantRelationship?.countryCode,
    infoListResult.hasResponse,
    lastPurchaseInfoResult.data?.getVariantPackLastPurchaseInfo,
    lastPurchaseInfoResult.hasResponse,
    orderGuidePriceEditDisplay?.data?.getPriceEditDisplayInfo
      .title_translations,
    orderGuidePriceEditDisplay?.data?.getPriceEditDisplayInfo
      .variant_pack_details,
    parentVariantPackGroupMap,
    priceEditDisplay?.data?.getPriceEditDisplayInfo.title_translations,
    priceEditDisplay?.data?.getPriceEditDisplayInfo.variant_pack_details,
    salesRepMode,
    updateSalesRepVariantPackInfo,
    updatedSalesRepInfoMap,
    variantPackConstraintsMap,
    variantPackGroupMap,
  ]);

  return (
    <VariantPackInfoContext.Provider value={value}>
      {children}
    </VariantPackInfoContext.Provider>
  );
};

export const DummyVariantPackInfoContextProvider: React.FC<Props> = ({
  children,
}) => {
  const {
    childVariantPackGroupMap,
    parentVariantPackGroupMap,
    variantPackGroupMap,
  } = useVariantPackGroup();

  const value = {
    childVariantPackGroupMap,
    currencyCode: DEFAULT_CURRENCY_CODE,
    essentialVariantPackUUIDs: new Set<string>(),
    getVariantPackInfo: () => {
      return null;
    },
    hasInfoListResponse: true,
    hasLastPurchaseInfoResponse: false,
    lastPurchaseInfoMap: {},
    parentVariantPackGroupMap,
    translatedTitles: {},
    updateSalesRepVariantPackInfo: () => null,
    variantPackDetails: {},
    variantPackGroupMap,
    variantPackPriceEditSettings: {},
  };

  return (
    <VariantPackInfoContext.Provider value={value}>
      {children}
    </VariantPackInfoContext.Provider>
  );
};
